import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CalendarBlank, Clock, ArrowRight, ArrowLeft, CheckCircle, VideoCamera, ShieldCheck,
} from "@phosphor-icons/react";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import { autoPrefixUS } from "../components/PhoneInput";

/**
 * Public scheduling page — Calendly-style slot picker.
 * Anyone with the link /schedule/{host_slug} can pick a free 30-min slot.
 */
export default function PublicSchedule() {
  const { hostSlug } = useParams();
  const nav = useNavigate();
  const [host, setHost] = useState(null);
  const [error, setError] = useState(null);
  const [slots, setSlots] = useState([]);
  const [picked, setPicked] = useState(null);
  const [step, setStep] = useState("date"); // date | time | form | done
  const [activeDay, setActiveDay] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      axios.get(`${BACKEND_URL}/api/availability/${hostSlug}`),
      axios.get(`${BACKEND_URL}/api/availability/${hostSlug}/slots?days=21`),
    ]).then(([h, s]) => {
      if (!alive) return;
      setHost(h.data);
      setSlots(s.data.open_slots);
      // pre-select the first available day
      const firstDay = s.data.open_slots[0]?.start?.slice(0, 10);
      if (firstDay) setActiveDay(firstDay);
    }).catch((e) => alive && setError(e?.response?.data?.detail || "Schedule unavailable"));
    return () => { alive = false; };
  }, [hostSlug]);

  // Group slots by day
  const slotsByDay = useMemo(() => {
    const m = new Map();
    for (const s of slots) {
      const d = s.start.slice(0, 10);
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(s);
    }
    return m;
  }, [slots]);
  const days = useMemo(() => Array.from(slotsByDay.keys()), [slotsByDay]);

  if (error) {
    return (
      <main className="min-h-screen bg-bone grid place-items-center px-6" data-testid="schedule-error">
        <div className="text-center max-w-sm">
          <img src="/logo.png" alt="" className="h-14 w-14 mx-auto" />
          <h1 className="mt-6 font-heading text-3xl font-black text-ink">Schedule unavailable</h1>
          <p className="mt-3 text-slate-500 text-sm">{error}</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-brandblue text-sm">
            <ArrowLeft size={14} /> Back to MedexOVA
          </Link>
        </div>
      </main>
    );
  }

  if (!host) {
    return <main className="min-h-screen bg-bone grid place-items-center text-sm text-slate-400">Loading…</main>;
  }

  const fmtDay = (iso) =>
    new Date(iso + "T12:00:00Z").toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  const submit = async () => {
    if (!picked || !form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/public/schedule/${hostSlug}`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        organization: form.organization || null,
        scheduled_at: picked.start,
        duration_minutes: host.slot_minutes || 30,
        notes: form.notes || null,
      });
      setConfirmed(data);
      setStep("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not book that slot — try another");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-bone" data-testid="public-schedule">
      <section className="relative overflow-hidden bg-brandblue-ink text-white">
        <div aria-hidden className="orb h-[460px] w-[460px] bg-brandblue/30 -top-40 -left-40" />
        <div aria-hidden className="orb h-[380px] w-[380px] bg-ova/25 -bottom-20 -right-10" />
        <div aria-hidden className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-3xl mx-auto px-6 pt-16 pb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue mb-6">
            <ArrowLeft size={12} /> MedexOVA
          </Link>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue inline-flex items-center gap-2">
            <VideoCamera size={12} weight="fill" /> Book a meeting · MedexOVA Meet
          </div>
          <h1 className="mt-3 font-heading text-3xl sm:text-5xl font-black">Schedule a call with {host.host_name}</h1>
          <p className="mt-3 text-white/70 text-sm max-w-xl">
            {host.slot_minutes}-minute video call · End-to-end encrypted · No download required.
            {host.timezone_label && <> Times shown in your local time zone, hosted from {host.timezone_label}.</>}
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-10">
        {step === "done" && confirmed ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-emerald-200 bg-white p-8 text-center shadow-sm" data-testid="schedule-confirmed">
            <CheckCircle size={42} weight="fill" className="text-emerald-500 mx-auto" />
            <h2 className="mt-4 font-heading text-3xl font-black text-ink">You're booked.</h2>
            <p className="mt-2 text-sm text-slate-600">
              {fmtDay(confirmed.scheduled_at.slice(0, 10))} at {fmtTime(confirmed.scheduled_at)} ·{" "}
              {confirmed.duration_minutes} min with {confirmed.host_name}.
            </p>
            <p className="mt-1 text-xs text-slate-500">A calendar invite was sent to <b>{form.email}</b>.</p>
            <div className="mt-6 flex justify-center gap-3">
              <a
                href={`/meet/${confirmed.room_id}`}
                className="inline-flex items-center gap-2 bg-brandblue hover:bg-brandblue-hover text-white font-semibold px-5 py-3 rounded-md text-sm"
              >
                Open meeting room <ArrowRight size={14} weight="bold" />
              </a>
              <a
                href={`${BACKEND_URL}/api/meetings/${confirmed.id}/ics`}
                className="inline-flex items-center gap-2 border border-slate-200 hover:border-brandblue text-slate-700 font-semibold px-5 py-3 rounded-md text-sm"
              >
                Add to calendar
              </a>
            </div>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-[1fr_2fr] gap-6">
            {/* Day list */}
            <aside className="md:sticky md:top-6 self-start rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue inline-flex items-center gap-2">
                <CalendarBlank size={12} /> Pick a day
              </div>
              <div className="mt-3 space-y-1 max-h-[420px] overflow-y-auto">
                {days.length === 0 && (
                  <div className="text-xs text-slate-500">No open slots in the next 21 days.</div>
                )}
                {days.map((d) => (
                  <button
                    key={d}
                    onClick={() => { setActiveDay(d); setStep("time"); }}
                    data-testid={`schedule-day-${d}`}
                    className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm transition ${
                      activeDay === d ? "border-brandblue bg-brandblue/5" : "border-slate-200 hover:border-brandblue/40 hover:bg-slate-50"
                    }`}
                  >
                    <div className="font-semibold text-ink">{fmtDay(d)}</div>
                    <div className="text-[11px] text-slate-500">{(slotsByDay.get(d) || []).length} slots</div>
                  </button>
                ))}
              </div>
            </aside>

            {/* Slot grid + form */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 min-h-[420px]">
              {step !== "form" && activeDay && (
                <>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue inline-flex items-center gap-2">
                    <Clock size={12} /> {fmtDay(activeDay)}
                  </div>
                  <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {(slotsByDay.get(activeDay) || []).map((s) => (
                      <button
                        key={s.start}
                        onClick={() => { setPicked(s); setStep("form"); }}
                        data-testid={`schedule-slot-${s.start}`}
                        className="text-sm font-semibold border border-slate-200 hover:border-brandblue hover:bg-brandblue/5 rounded-md px-3 py-2.5"
                      >
                        {fmtTime(s.start)}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {step === "form" && picked && (
                <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} data-testid="schedule-form">
                  <button onClick={() => setStep("time")} className="text-[11px] uppercase tracking-[0.18em] text-slate-500 hover:text-ink inline-flex items-center gap-1">
                    <ArrowLeft size={11} /> Pick a different time
                  </button>
                  <div className="mt-2 text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue inline-flex items-center gap-2">
                    <CalendarBlank size={12} /> {fmtDay(picked.start.slice(0, 10))} · {fmtTime(picked.start)} · {host.slot_minutes} min
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="schedule-name" />
                    <Field label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="schedule-email" />
                    <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })}
                           onBlur={() => setForm((f) => ({ ...f, phone: autoPrefixUS(f.phone) }))}
                           testid="schedule-phone" />
                    <Field label="Organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="schedule-org" />
                  </div>
                  <div className="mt-3">
                    <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500">Anything we should know?</label>
                    <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="mt-1 w-full text-sm rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:border-brandblue" />
                  </div>
                  <button
                    onClick={submit}
                    disabled={submitting}
                    data-testid="schedule-confirm"
                    className="mt-4 inline-flex items-center gap-2 bg-brandblue hover:bg-brandblue-hover text-white font-semibold px-5 py-3 rounded-md text-sm disabled:opacity-60"
                  >
                    {submitting ? "Booking…" : <>Confirm booking <ArrowRight size={14} weight="bold" /></>}
                  </button>
                  <p className="mt-3 text-[11px] text-slate-400 inline-flex items-center gap-1">
                    <ShieldCheck size={11} /> Calendar invite sent automatically · No account needed
                  </p>
                </motion.div>
              )}
              {step === "date" && !activeDay && (
                <div className="text-sm text-slate-500">Pick a day on the left to see open times.</div>
              )}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, onBlur }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        required={required}
        data-testid={testid}
        className="mt-1 w-full text-sm rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:border-brandblue"
      />
    </div>
  );
}
